import clsx, { type ClassValue } from 'clsx';
import { formatDistanceToNowStrict } from 'date-fns';
import { formatInZone, zonedDayDiff, zonedParts } from '@itsappy/shared';
import { activeTimezone } from '@/lib/time';

export const cn = (...inputs: ClassValue[]) => clsx(inputs);

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function relativeTime(iso?: string): string {
  if (!iso) return '';
  return `${formatDistanceToNowStrict(new Date(iso))} ago`;
}

/**
 * Every function below reads the zone at call time rather than closing over
 * it, so a timezone change in Settings reaches the next render without any of
 * the ~40 call sites knowing this setting exists.
 *
 * "Today" and "yesterday" are calendar questions, and the calendar in question
 * is the reader's. Comparing `Date` objects answers them for the *device*,
 * which is why they go through `zonedDayDiff`.
 */

const clock = { hour: '2-digit', minute: '2-digit', hour12: false } as const;

/** Slack-style timestamp: time today, "Yesterday", then a date. */
export function messageTime(iso: string): string {
  const zone = activeTimezone();
  const days = zonedDayDiff(iso, Date.now(), zone);
  const time = formatInZone(iso, zone, clock);
  if (days === 0) return time;
  if (days === -1) return `Yesterday ${time}`;
  const sameYear = zonedParts(iso, zone).year === zonedParts(Date.now(), zone).year;
  const date = formatInZone(iso, zone, { month: 'short', day: 'numeric' });
  return sameYear
    ? `${date}, ${time}`
    : `${date} ${zonedParts(iso, zone).year}, ${time}`;
}

export function dayLabel(iso: string): string {
  const zone = activeTimezone();
  const days = zonedDayDiff(iso, Date.now(), zone);
  if (days === 0) return 'Today';
  if (days === -1) return 'Yesterday';
  const sameYear = zonedParts(iso, zone).year === zonedParts(Date.now(), zone).year;
  return formatInZone(
    iso,
    zone,
    sameYear
      ? { weekday: 'long', month: 'long', day: 'numeric' }
      : { month: 'long', day: 'numeric', year: 'numeric' },
  );
}

export function dueLabel(
  iso?: string,
  status?: string,
): { text: string; tone: 'overdue' | 'soon' | 'normal' } | null {
  if (!iso) return null;
  const zone = activeTimezone();
  // Finished work cannot be late any more: show the date it was due, plainly.
  if (status === 'done') {
    return { text: formatInZone(iso, zone, { month: 'short', day: 'numeric' }), tone: 'normal' };
  }
  // Counted in whole calendar days, not 24-hour blocks: a deadline at 09:00
  // tomorrow is "due tomorrow" whether you read it at 08:00 or at 23:00.
  const days = zonedDayDiff(iso, Date.now(), zone);
  if (days < 0) return { text: `${Math.abs(days)}d overdue`, tone: 'overdue' };
  if (days === 0) return { text: 'Due today', tone: 'soon' };
  if (days === 1) return { text: 'Due tomorrow', tone: 'soon' };
  if (days <= 7) return { text: `Due in ${days}d`, tone: 'normal' };
  return { text: formatInZone(iso, zone, { month: 'short', day: 'numeric' }), tone: 'normal' };
}

/** `09:30` — a wall-clock time in the reader's zone. */
export function clockTime(iso: string): string {
  return formatInZone(iso, activeTimezone(), clock);
}

/**
 * `Good morning` / `Good afternoon` / `Good evening`.
 *
 * Read off the reader's clock, not the device's. Somebody working a European
 * day from a laptop still set to New York should be greeted by the day they
 * are actually having.
 */
export function timeOfDay(): string {
  const { hour } = zonedParts(Date.now(), activeTimezone());
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** `Thursday, 20 August` — today, in the reader's zone and their locale's order. */
export function todayLabel(): string {
  return formatInZone(Date.now(), activeTimezone(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** `2026-08-20` — today's calendar date, for a `<time>` element's `dateTime`. */
export function todayIso(): string {
  const { year, month, day } = zonedParts(Date.now(), activeTimezone());
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** `17 Aug 2026, 09:30` — an unambiguous full timestamp, for tooltips. */
export function fullTime(iso: string): string {
  return formatInZone(iso, activeTimezone(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...clock,
  });
}

/**
 * The server's compact cached preview for a locally uploaded image.
 *
 * Anything that is not one of our own `/uploads` URLs comes back untouched, so
 * this is safe to wrap around any `src`.
 */
export function thumbnailUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    const match = parsed.pathname.match(/^\/uploads\/([^/]+)$/);
    if (!match) return url;
    // Keep previews on the current product origin. Reusing the existing HTTP/2
    // connection is noticeably faster than opening lab.itsappy.com from Chat.
    // The query carries the media pass, and /uploads/thumb wants it too.
    return `${window.location.origin}/uploads/thumb/${match[1]}${parsed.search}`;
  } catch {
    return url;
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

/** Renders `<@userId>` mention tokens as readable names. */
export function renderMentions(body: string, names: Map<string, string>): string {
  return body.replace(/<@([a-f\d]{24})>/gi, (_m, id: string) => `@${names.get(id) ?? 'someone'}`);
}

export function debounce<T extends (...args: never[]) => void>(fn: T, ms: number) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

/** Caps how often a function runs — used for movement and cursor broadcasts. */
export function throttle<T extends (...args: never[]) => void>(fn: T, ms: number) {
  let last = 0;
  let pending: Parameters<T> | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const invoke = (args: Parameters<T>) => {
    last = Date.now();
    fn(...args);
  };

  return (...args: Parameters<T>) => {
    const elapsed = Date.now() - last;
    if (elapsed >= ms) {
      invoke(args);
      return;
    }
    pending = args;
    if (timer) return;
    timer = setTimeout(() => {
      timer = undefined;
      if (pending) invoke(pending);
      pending = null;
    }, ms - elapsed);
  };
}

/**
 * Paired with `.chip`. These are the semantic tones, not hand-rolled colours:
 * the raw dark-mode values they used to carry never inverted, so every
 * priority chip washed out to about 2:1 on the light theme's white cards.
 */
export const PRIORITY_STYLES: Record<string, { label: string; className: string }> = {
  urgent: { label: 'Urgent', className: 'tone-red' },
  high: { label: 'High', className: 'tone-orange' },
  medium: { label: 'Medium', className: 'tone-amber' },
  low: { label: 'Low', className: 'tone-sky' },
  none: { label: 'None', className: 'tone-slate' },
};

/** Chip styling per task type. `task` is the unmarked default — no chip. */
export const TYPE_STYLES: Record<string, { label: string; className: string }> = {
  task: { label: 'Task', className: 'tone-slate' },
  bug: { label: 'Bug', className: 'tone-red' },
  feature: { label: 'Feature', className: 'tone-sky' },
  improvement: { label: 'Improvement', className: 'tone-amber' },
  chore: { label: 'Chore', className: 'tone-slate' },
};

export const STATUS_LABELS: Record<string, string> = {
  backlog: 'Backlog',
  todo: 'To do',
  in_progress: 'In progress',
  in_review: 'In review',
  done: 'Done',
};

/** The same five, in Arabic. Paired with `STATUS_LABELS` by key. */
export const STATUS_LABELS_AR: Record<string, string> = {
  backlog: 'قائمة الانتظار',
  todo: 'للتنفيذ',
  in_progress: 'قيد التنفيذ',
  in_review: 'قيد المراجعة',
  done: 'مكتمل',
};

export function statusLabel(status: string, language: 'en' | 'ar' = 'en'): string {
  const labels = language === 'ar' ? STATUS_LABELS_AR : STATUS_LABELS;
  return labels[status] ?? STATUS_LABELS[status] ?? status;
}

/**
 * Wraps a run of text so the bidi algorithm cannot reorder it against the
 * surrounding paragraph.
 *
 * Needed because the app is a mix: the shell is translated but most strings
 * are still English, and an English phrase carrying digits — "0 done of 3",
 * "2/3" — is a sequence of weak and neutral characters to the bidi algorithm.
 * Dropped into an RTL page it comes out reordered, which is how "0 done of 3"
 * became "of 3 done 0". U+2068 FIRST STRONG ISOLATE opens a run whose
 * direction is taken from its own first strong character, and U+2069 closes
 * it, so the run reads correctly whichever way the page runs.
 */
export function isolate(text: string): string {
  // Written as escapes rather than literals: both characters are invisible,
  // and a stray edit that dropped one would be silent in review and obvious
  // only to an Arabic reader.
  return `\u2068${text}\u2069`;
}

/** `3 tasks` / `1 task` — pluralises a count against its noun. */
export function countLabel(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}
